import type { CheckinPhotoRef } from '@awwyeah/shared';
import { openOutboxStores } from './idb';
import { CheckinOutbox } from './photoOutbox';
import type { AnnounceOutboxRecord, OutboxRecord, OutboxStore, PhotoOutboxRecord } from './store';
import { MemoryOutboxStore } from './store';

export type { PhotoOutboxRecord, AnnounceOutboxRecord } from './store';
export { CheckinOutbox } from './photoOutbox';

/**
 * The app's single outbox, started on boot.
 *
 * Opening IndexedDB is asynchronous but posting must not be, so every store call is routed
 * through `LazyOutboxStore`: the queue can enqueue a photo the instant the shutter closes,
 * and the write lands as soon as the database is open (or in memory, if this browser
 * refuses to give us one).
 */

class LazyOutboxStore<T extends OutboxRecord> implements OutboxStore<T> {
  constructor(private readonly resolved: Promise<OutboxStore<T>>) {}
  async put(record: T): Promise<void> {
    return (await this.resolved).put(record);
  }
  async get(id: string): Promise<T | undefined> {
    return (await this.resolved).get(id);
  }
  async all(): Promise<T[]> {
    return (await this.resolved).all();
  }
  async delete(id: string): Promise<void> {
    return (await this.resolved).delete(id);
  }
}

const uploadedHandlers = new Set<(record: PhotoOutboxRecord) => void>();
const uploadingHandlers = new Set<(record: PhotoOutboxRecord) => void>();
const changeListeners = new Set<() => void>();

let outbox: CheckinOutbox | null = null;
let pendingSnapshot: PhotoOutboxRecord[] = [];

function notifyChanged(): void {
  void outbox?.pendingPhotos().then((records) => {
    // Same-reference snapshots keep `useSyncExternalStore` from looping.
    const changed =
      records.length !== pendingSnapshot.length ||
      records.some((record, index) => pendingSnapshot[index]?.id !== record.id);
    if (!changed) return;
    pendingSnapshot = records;
    for (const listener of changeListeners) listener();
  });
}

export function getCheckinOutbox(): CheckinOutbox {
  if (outbox) return outbox;

  const stores = openOutboxStores();
  const photos = new LazyOutboxStore<PhotoOutboxRecord>(
    stores.then((s) => s?.photos ?? new MemoryOutboxStore<PhotoOutboxRecord>()),
  );
  const announcements = new LazyOutboxStore<AnnounceOutboxRecord>(
    stores.then((s) => s?.announcements ?? new MemoryOutboxStore<AnnounceOutboxRecord>()),
  );

  outbox = new CheckinOutbox({
    photos,
    announcements,
    onPhotoUploaded: (record) => {
      for (const handler of uploadedHandlers) handler(record);
      notifyChanged();
    },
    onPhotoUploading: (record) => {
      for (const handler of uploadingHandlers) handler(record);
    },
    onChange: notifyChanged,
  });
  outbox.start();
  outbox.listenForOnline();
  notifyChanged();
  return outbox;
}

/** Test/teardown seam. */
export function resetCheckinOutboxForTests(): void {
  outbox?.stop();
  outbox = null;
  pendingSnapshot = [];
  uploadedHandlers.clear();
  uploadingHandlers.clear();
  changeListeners.clear();
}

export function subscribeToOutbox(listener: () => void): () => void {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

export function pendingPhotosSnapshot(): PhotoOutboxRecord[] {
  return pendingSnapshot;
}

/** Fires when bytes reach the server — the module flips `photoRef.state` in Yjs here. */
export function onPhotoUploaded(handler: (record: PhotoOutboxRecord) => void): () => void {
  uploadedHandlers.add(handler);
  return () => uploadedHandlers.delete(handler);
}

export function onPhotoUploading(handler: (record: PhotoOutboxRecord) => void): () => void {
  uploadingHandlers.add(handler);
  return () => uploadingHandlers.delete(handler);
}

/** The photo bytes for a post still waiting on this device, if we have them. */
export async function localPhotoBlob(photoId: string): Promise<Blob | null> {
  const record = pendingSnapshot.find((entry) => entry.id === photoId);
  if (record) return record.blob;
  const found = await getCheckinOutbox()
    .pendingPhotos()
    .then((records) => records.find((entry) => entry.id === photoId));
  return found?.blob ?? null;
}

/** `photoRef` for bytes that are now on the server. */
export function uploadedPhotoRef(ref: CheckinPhotoRef): CheckinPhotoRef {
  return { ...ref, state: 'uploaded' };
}
